import { useEffect, useRef, useState } from "react";
import { Camera, Mic, MicOff, Phone, PhoneOff, SwitchCamera, Video, VideoOff } from "lucide-react";
import { useCall } from "../context/CallContext";
import { Avatar } from "./Avatar";
import { ru } from "../locales/ru";

const fmt = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const circleBtn =
  "w-16 h-16 rounded-full flex items-center justify-center active:scale-[0.94] transition-transform";

const VideoStage = ({ remoteStream, localStream, swapped, onSwap }) => {
  const bigRef = useRef(null);
  const smallRef = useRef(null);

  useEffect(() => {
    const big = swapped ? localStream : remoteStream;
    const small = swapped ? remoteStream : localStream;
    if (bigRef.current) bigRef.current.srcObject = big || null;
    if (smallRef.current) smallRef.current.srcObject = small || null;
  }, [remoteStream, localStream, swapped]);

  return (
    <div className="absolute inset-0" data-testid="video-stage">
      <video
        ref={bigRef}
        data-testid="video-remote"
        autoPlay
        playsInline
        muted={swapped}
        className="w-full h-full object-cover bg-neutral-900"
      />
      <button
        onClick={onSwap}
        data-testid="video-swap-button"
        className="absolute top-6 right-4 w-[104px] h-[152px] rounded-2xl overflow-hidden border border-white/20 shadow-lg bg-neutral-800"
      >
        <video
          ref={smallRef}
          data-testid="video-local"
          autoPlay
          playsInline
          muted={!swapped}
          className="w-full h-full object-cover"
        />
      </button>
    </div>
  );
};

export const CallOverlay = () => {
  const {
    call, peer, phase, muted, media, cameraOn, localStream, remoteStream,
    acceptCall, declineCall, hangUp, toggleMute, toggleCamera, switchCamera,
  } = useCall();
  const [seconds, setSeconds] = useState(0);
  const [swapped, setSwapped] = useState(false);

  useEffect(() => {
    if (phase !== "active" || !call?.answered_at) {
      setSeconds(0);
      return;
    }
    const started = new Date(call.answered_at).getTime();
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, call?.answered_at]);

  useEffect(() => {
    if (phase === "idle") setSwapped(false);
  }, [phase]);

  if (phase === "idle") return null;

  const isVideo = media === "video";
  const showStage = isVideo && phase === "active";
  const status =
    phase === "incoming"
      ? isVideo
        ? ru.calls.incomingVideo
        : ru.calls.incoming
      : phase === "outgoing"
      ? ru.calls.calling
      : fmt(seconds);

  return (
    <div
      data-testid={phase === "incoming" ? "incoming-call-overlay" : "in-call-view"}
      data-media={media}
      className="fixed inset-0 z-[60] bg-black text-white flex flex-col items-center justify-center px-8"
    >
      {showStage && (
        <VideoStage
          remoteStream={remoteStream}
          localStream={localStream}
          swapped={swapped}
          onSwap={() => setSwapped((s) => !s)}
        />
      )}

      <div className={`relative z-10 flex flex-col items-center ${showStage ? "mt-auto mb-6" : ""}`}>
        {!showStage && <Avatar avatar={peer?.avatar} name={peer?.name} size={112} testId="call-peer-avatar" />}
        <p
          className={`${showStage ? "text-lg" : "mt-6 text-2xl"} font-extrabold tracking-tight drop-shadow`}
          data-testid="call-peer-name"
        >
          {peer?.name || ru.calls.unknown}
        </p>
        <p className="mt-1.5 text-white/70 text-sm flex items-center gap-1.5" data-testid="call-status">
          {isVideo && phase !== "active" && <Camera size={13} data-testid="video-indicator" />}
          {status}
        </p>

        {phase === "incoming" ? (
          <div className="flex items-center gap-10 mt-16">
            <button data-testid="call-decline-button" onClick={declineCall} className={`${circleBtn} bg-white/10`}>
              <PhoneOff size={24} />
            </button>
            <button data-testid="call-accept-button" onClick={acceptCall} className={`${circleBtn} bg-[#34C759]`}>
              {isVideo ? <Video size={24} /> : <Phone size={24} />}
            </button>
          </div>
        ) : (
          <div className={`flex items-center ${isVideo ? "gap-5" : "gap-10"} mt-14`}>
            <button
              data-testid="call-mute-button"
              onClick={toggleMute}
              aria-pressed={muted}
              className={`${circleBtn} ${muted ? "bg-white text-black" : "bg-white/10"}`}
            >
              {muted ? <MicOff size={24} /> : <Mic size={24} />}
            </button>
            {isVideo && (
              <>
                <button
                  data-testid="call-camera-button"
                  onClick={toggleCamera}
                  aria-pressed={!cameraOn}
                  className={`${circleBtn} ${cameraOn ? "bg-white/10" : "bg-white text-black"}`}
                >
                  {cameraOn ? <Video size={24} /> : <VideoOff size={24} />}
                </button>
                <button data-testid="call-switch-camera-button" onClick={switchCamera} className={`${circleBtn} bg-white/10`}>
                  <SwitchCamera size={22} />
                </button>
              </>
            )}
            <button data-testid="call-hangup-button" onClick={hangUp} className={`${circleBtn} bg-[#FF3B30]`}>
              <PhoneOff size={24} />
            </button>
          </div>
        )}
      </div>

      {!showStage && <p className="mt-10 text-[11px] text-white/40 text-center">{ru.calls.p2pNote}</p>}
    </div>
  );
};
